import OneSignal from 'onesignal-cordova-plugin';
import { isNativeNotWeb, isIosNativeOnly } from '@/utils/platform-check';
import { redirectDeepLinkPath } from '@/utils/redirect-uri';
import router from '@/router';
import { readCachedProfile, isCachedProfileFresh } from '@/utils/mixpanel';
import { openExternal, openUrl } from '@/utils/browser-helper';

/**
 * Subscribers to notification taps (#1692).
 *
 * A registry rather than an import of the store: this module is loaded at boot
 * by the app shell, and reaching into `@/store` from here would make a cycle
 * (store → modules → utils → store) out of what is a one-way notification.
 * Whoever cares subscribes; nobody here has to know why.
 */
const tapSubscribers = new Set();

/**
 * Called for every notification tap, with what the payload said it was.
 *
 * `kind` is `additionalData.kind`, which the API sets on the pushes it owns
 * (api#1139). It is undefined for a push that predates it or comes from
 * another feature, and a subscriber that acts on a specific kind therefore
 * does nothing for those — which is the intent, not a gap.
 *
 * @param {(tap: { kind: string | undefined, link: string | undefined }) => void} handler
 * @returns {() => void} unsubscribe
 */
export function onNotificationTap(handler) {
  tapSubscribers.add(handler);
  return () => tapSubscribers.delete(handler);
}

function publishTap(tap) {
  for (const handler of tapSubscribers) {
    try {
      handler(tap);
    } catch (error) {
      // One bad subscriber must not stop the tap from being handled, and must
      // not stop the OTHER subscribers either.
      console.warn('[OneSignal] A notification-tap subscriber threw:', error);
    }
  }
}

/**
 * The OneSignal App ID — which OneSignal app this install belongs to. It is
 * public by design: `OneSignal.initialize(appId)` is the SDK's only credential
 * and it ships in every bundle. It is NOT the REST API key, which authorises
 * sends and lives only on the servers that call onesignal.com (hogc-one-api,
 * app-notifications).
 *
 * Set in three places that must agree by name: the GitHub environment the OTA
 * lane builds from (ci-env.sh refuses to build without it), the Appflow
 * environment that builds the store binary, and SSM for the Amplify web build.
 */
const ONESIGNAL_APP_ID = process.env.VUE_APP_ONESIGNAL_APP_ID;

let oneSignalInitialized = false;
let oneSignalListenersAttached = false;
let ensureSubscriptionScheduled = false;

function canUseOneSignal() {
  return isNativeNotWeb() && ONESIGNAL_APP_ID;
}

function getFreshCachedMemberId() {
  try {
    if (typeof window === 'undefined') return null;
    const cached = readCachedProfile();
    if (!cached) {
      return null;
    }
    if (!isCachedProfileFresh(cached)) {
      console.warn(
        '[Fix-C OneSignal Cache] Cached profile stale (>30d or no timestamp); skipping login',
      );
      return null;
    }
    return cached.memberId ? String(cached.memberId) : null;
  } catch (e) {
    console.warn('[Fix-C OneSignal Cache] Failed to read cached member ID:', e);
    return null;
  }
}

function resolveMemberId(memberId) {
  if (memberId) {
    return String(memberId);
  }
  return getFreshCachedMemberId();
}

export function initializeNotification() {
  if (!canUseOneSignal()) {
    return;
  }

  const cachedMemberId = getFreshCachedMemberId();
  if (!cachedMemberId) {
    console.log('[OneSignal] Delaying initialization until member ID is available');
    return;
  }

  identifyOneSignalMember(cachedMemberId);
  console.log('[Fix-C OneSignal Cache] Logged in from cache:', cachedMemberId);
}

export async function requestNotificationPermission(memberId) {
  if (!canUseOneSignal()) {
    return;
  }

  const resolvedMemberId = resolveMemberId(memberId);
  if (!resolvedMemberId) {
    console.log('[OneSignal] Skipping permission request until member ID is available');
    return;
  }

  identifyOneSignalMember(resolvedMemberId, null, { ensureSubscription: false });
  await oneSignalRequestNotificationPermission();
  scheduleOneSignalEnsureSubscription();
}

export async function oneSignalRequestNotificationPermission() {
  await OneSignal.Notifications.requestPermission();
}

export async function canRequestNotificationsPermission(memberId) {
  if (!canUseOneSignal()) {
    return false;
  }

  const resolvedMemberId = resolveMemberId(memberId);
  if (!resolvedMemberId) {
    return false;
  }

  identifyOneSignalMember(resolvedMemberId, null, { ensureSubscription: false });
  return OneSignal.Notifications.canRequestPermission();
}

export function identifyOneSignalMember(memberId, tags, options = {}) {
  if (!canUseOneSignal() || !memberId) {
    return false;
  }

  oneSignalInitializeNotification();
  OneSignal.login(String(memberId));
  if (tags) {
    OneSignal.User.addTags({ ...tags });
  }
  if (options.ensureSubscription !== false) {
    scheduleOneSignalEnsureSubscription();
  }
  return true;
}

/**
 * Get the OneSignal subscription ID if it's valid (not in "Never Subscribed" state)
 * Returns null if subscription is invalid or not available
 */
export async function getValidSubscriptionId() {
  if (!canUseOneSignal()) {
    return null;
  }

  try {
    const pushSubscription = OneSignal.User?.pushSubscription;
    if (!pushSubscription) {
      return null;
    }

    // Use getIdAsync() instead of deprecated getId()
    // Check if getIdAsync method exists, fallback to getId for older SDK versions
    let subscriptionId = null;
    if (typeof pushSubscription.getIdAsync === 'function') {
      subscriptionId = await pushSubscription.getIdAsync();
    } else if (typeof pushSubscription.getId === 'function') {
      // Fallback for older SDK versions
      subscriptionId = await pushSubscription.getId();
    } else {
      console.warn('[OneSignal] Neither getIdAsync nor getId method available on pushSubscription');
      return null;
    }
    const isOptedIn = await pushSubscription.optedIn;

    // Only return subscription ID if it's valid and opted in
    if (subscriptionId && subscriptionId.trim().length > 0 && isOptedIn) {
      return subscriptionId;
    }

    return null;
  } catch (error) {
    console.warn('[OneSignal] Error getting subscription ID:', error);
    return null;
  }
}

/**
 * A read-only snapshot of this install's OneSignal state, for dev harnesses.
 * `externalId` is what a server-side send must target for this device to get
 * the push, so it is the first thing to check when a reminder never arrives.
 * Each field is read on its own so one SDK call failing does not hide the rest.
 */
export async function getOneSignalDebugState() {
  const state = {
    native: isNativeNotWeb(),
    appIdSet: Boolean(ONESIGNAL_APP_ID),
    initialized: oneSignalInitialized,
    externalId: null,
    oneSignalId: null,
    permission: null,
    subscriptionId: null,
    optedIn: null,
  };
  if (!canUseOneSignal() || !oneSignalInitialized) return state;

  const read = async fn => {
    try {
      return await fn();
    } catch (error) {
      console.warn('[OneSignal] Debug read failed:', error);
      return null;
    }
  };
  state.externalId = await read(() => OneSignal.User.getExternalId());
  state.oneSignalId = await read(() => OneSignal.User.getOnesignalId());
  state.permission = await read(() => OneSignal.Notifications.getPermissionAsync());
  state.subscriptionId = await read(() => OneSignal.User.pushSubscription.getIdAsync());
  state.optedIn = await read(() => OneSignal.User.pushSubscription.getOptedInAsync());
  return state;
}

export function oneSignalInitializeNotification() {
  if (!oneSignalInitialized) {
    OneSignal.initialize(ONESIGNAL_APP_ID);
    oneSignalInitialized = true;
  }

  if (oneSignalListenersAttached) {
    return;
  }
  oneSignalListenersAttached = true;

  // Listen for subscription changes to handle late activation
  OneSignal.User?.pushSubscription?.addEventListener('change', async () => {
    try {
      const pushSubscription = OneSignal.User?.pushSubscription;
      if (pushSubscription) {
        // Use getIdAsync() instead of deprecated getId()
        let subscriptionId = null;
        if (typeof pushSubscription.getIdAsync === 'function') {
          subscriptionId = await pushSubscription.getIdAsync();
        } else if (typeof pushSubscription.getId === 'function') {
          // Fallback for older SDK versions
          subscriptionId = await pushSubscription.getId();
        } else {
          console.warn(
            '[OneSignal] Neither getIdAsync nor getId method available on pushSubscription in change listener',
          );
          return;
        }
        const isOptedIn = await pushSubscription.optedIn;
        console.log('[OneSignal] Subscription changed:', {
          subscriptionId,
          isOptedIn,
        });

        // If subscription becomes valid but not opted in, opt in
        if (subscriptionId && subscriptionId.trim().length > 0 && !isOptedIn) {
          await pushSubscription.optIn();
          console.log('[OneSignal] Auto-opted in after subscription change');
        }
      }
    } catch (error) {
      console.warn('[OneSignal] Error handling subscription change:', error);
    }
  });

  // Handle notification clicks
  OneSignal.Notifications.addEventListener('click', async event => {
    try {
      console.log('[OneSignal] Notification clicked:', event);
      const notification = event.notification || (event.getNotification && event.getNotification());

      if (!notification) {
        console.warn('[OneSignal] No notification object found in click event');
        return;
      }

      // Priority: Check additionalData.link first (for giving-api notifications)
      // Then fall back to launchURL (for standard OneSignal notifications)
      const link = notification.additionalData?.link || notification.launchURL;

      // Published BEFORE the link is acted on, and before the `!link` return:
      // a subscriber's work does not depend on where the tap navigates, and a
      // payload that carries a kind but no link is still a tap that happened.
      publishTap({ kind: notification.additionalData?.kind, link });

      if (!link) {
        console.log('[OneSignal] No link found in notification');
        return;
      }

      console.log('[OneSignal] Handling notification link:', link);

      // Check if it's an internal link (heartofgodchurch.org domain)
      const isInternal = redirectDeepLinkPath(router, link);

      if (!isInternal) {
        // External link - open in browser (iOS) or new tab (Android/web)
        if (isIosNativeOnly()) {
          await openUrl(
            { url: link },
            {
              title: 'Push Notification',
              source: 'onesignal',
              page: 'Notification',
            },
          );
        } else {
          // Android or web - open in new tab/window
          openExternal(link, '_blank', {
            title: 'Push Notification',
            source: 'onesignal',
            page: 'Notification',
          });
        }
      } else {
        console.log('[OneSignal] Internal link handled by router');
      }
    } catch (error) {
      console.error('[OneSignal] Error handling notification click:', error);
    }
  });
}

function scheduleOneSignalEnsureSubscription() {
  if (ensureSubscriptionScheduled) {
    return;
  }
  ensureSubscriptionScheduled = true;
  // Subscription check still waits so permission + subscription state have
  // time to settle inside the native SDK.
  setTimeout(() => {
    oneSignalEnsureSubscription();
  }, 1000);
}

async function oneSignalEnsureSubscription() {
  try {
    // Request permission first (this is required for valid subscriptions)
    const permissionGranted = await OneSignal.Notifications.requestPermission();

    if (!permissionGranted) {
      console.log('[OneSignal] Permission not granted, skipping subscription activation');
      return;
    }

    // Wait for OneSignal to process permission and initialize subscription
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Check if push subscription exists
    const pushSubscription = OneSignal.User?.pushSubscription;
    if (!pushSubscription) {
      console.warn('[OneSignal] Push subscription not available yet');
      return;
    }

    // Use getIdAsync() instead of deprecated getId()
    let subscriptionId = null;
    try {
      if (typeof pushSubscription.getIdAsync === 'function') {
        subscriptionId = await pushSubscription.getIdAsync();
      } else if (typeof pushSubscription.getId === 'function') {
        // Fallback for older SDK versions
        subscriptionId = await pushSubscription.getId();
      } else {
        console.warn(
          '[OneSignal] Neither getIdAsync nor getId method available, attempting opt-in directly',
        );
        // Try to opt-in directly without checking subscription ID
        try {
          const isOptedIn = await pushSubscription.optedIn;
          if (!isOptedIn) {
            await pushSubscription.optIn();
            console.log(
              '[OneSignal] Successfully opted in to push notifications (without subscription ID check)',
            );
          }
        } catch (optInError) {
          console.error('[OneSignal] Error opting in without subscription ID:', optInError);
        }
        return;
      }
    } catch (idError) {
      console.warn(
        '[OneSignal] Error getting subscription ID, attempting opt-in directly:',
        idError,
      );
      // If getting ID fails, try to opt-in anyway
      try {
        const isOptedIn = await pushSubscription.optedIn;
        if (!isOptedIn) {
          await pushSubscription.optIn();
          console.log('[OneSignal] Successfully opted in to push notifications (after ID error)');
        }
      } catch (optInError) {
        console.error('[OneSignal] Error opting in after ID error:', optInError);
      }
      return;
    }

    try {
      // Subscription ID retrieved successfully, verify it's valid

      // If subscription ID exists and is valid, ensure we're opted in
      if (subscriptionId && subscriptionId.trim().length > 0) {
        const isOptedIn = await pushSubscription.optedIn;
        if (!isOptedIn) {
          await pushSubscription.optIn();
          console.log('[OneSignal] Successfully opted in to push notifications');
        } else {
          console.log('[OneSignal] Already opted in to push notifications');
        }
      } else {
        // Invalid subscription ID - likely "Never Subscribed" state
        console.warn('[OneSignal] Invalid subscription ID detected, resetting subscription');

        // Opt out first to clear invalid state
        try {
          await pushSubscription.optOut();
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (optOutError) {
          console.warn('[OneSignal] Error during opt-out (may not be necessary):', optOutError);
        }

        // Then opt in again to create a fresh subscription
        await pushSubscription.optIn();
        console.log('[OneSignal] Reset and re-opted in to push notifications');
      }
    } catch (subscriptionError) {
      console.error('[OneSignal] Error checking/updating subscription:', subscriptionError);
      // Fallback: try to opt-in anyway
      try {
        await pushSubscription.optIn();
      } catch (fallbackError) {
        console.error('[OneSignal] Fallback opt-in also failed:', fallbackError);
      }
    }
  } catch (error) {
    console.warn('[OneSignal] ensureSubscription failed:', error);
  }
}
